import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import InfoHint from "../common/InfoHint";

const TeamStatCard = ({
  label,
  value,
  unit,
  subtitle,
  footerLeft,
  footerRight,
  valueColor = "#111827",
  footerRightColor = "#111827",
  peerComparison, // { value: number (percentage), peerValue: number }
  loading = false,
  info, // optional explanation, shown as an ⓘ next to the label
}) => {
  const { t } = useTranslation();
  // Determine peer comparison display
  const renderPeerBadge = () => {
    if (!peerComparison) return null;

    const { value: diff } = peerComparison;
    const isAbove = diff > 0;
    const isNeutral = Math.abs(diff) < 3;

    let bgColor, textColor, Icon;
    if (isNeutral) {
      bgColor = "#F3F4F6";
      textColor = "#6B7280";
      Icon = Minus;
    } else if (isAbove) {
      bgColor = "#FEF3E7";
      textColor = "#E07A3A";
      Icon = TrendingUp;
    } else {
      bgColor = "#F0FDF4";
      textColor = "#10B981";
      Icon = TrendingDown;
    }

    return (
      <div
        className="flex items-center gap-1 rounded px-1.5 py-0.5"
        style={{ backgroundColor: bgColor }}
      >
        <Icon size={10} style={{ color: textColor }} />
        <span className="text-[10px] font-medium" style={{ color: textColor }}>
          {isNeutral ? t("team.statCard.avg") : `${Math.abs(diff)}%`}
        </span>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white p-4">
      {/* Label with peer badge */}
      <div className="mb-2 flex min-h-[18px] items-center justify-between">
        <p className="flex items-center gap-1 text-xs text-[#6B7280]">
          {label}
          {info && <InfoHint text={info} />}
        </p>
        {!loading && renderPeerBadge()}
      </div>

      {/* Value — same 28px line box, shimmer bar while loading */}
      {loading ? (
        <div className="flex items-center" style={{ height: "34px" }}>
          <div className="shimmer h-7 w-16 rounded-md" />
        </div>
      ) : (
        <div className="flex items-baseline gap-0.5">
          <span
            className="text-[28px] font-bold leading-tight"
            style={{ color: valueColor }}
          >
            {value}
          </span>
          {unit && (
            <span className="text-sm font-medium text-[#9CA3AF]">{unit}</span>
          )}
        </div>
      )}
      {loading
        ? subtitle && (
            <div className="mt-1 flex h-[15px] items-center">
              <div className="shimmer h-2.5 w-24 rounded" />
            </div>
          )
        : subtitle && (
            <p className="mt-1 truncate text-[11px] text-[#9CA3AF]">
              {subtitle}
            </p>
          )}

      {/* Footer */}
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-[#F3F4F6] pt-3">
        <span
          className="min-w-0 truncate text-xs text-[#9CA3AF]"
          title={typeof footerLeft === "string" ? footerLeft : undefined}
        >
          {footerLeft}
        </span>
        {loading ? (
          <div className="shimmer h-2.5 w-16 shrink-0 rounded" />
        ) : (
          <span
            className="shrink-0 whitespace-nowrap text-xs font-medium"
            style={{ color: footerRightColor }}
          >
            {footerRight}
          </span>
        )}
      </div>
    </div>
  );
};

export default TeamStatCard;
